import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import isArray from 'isarray';

import { KEY_CODES } from 'legacy/src/utility/utils';
import { BOOKMARKED_QUESTIONS } from 'legacy/src/constants/common';
import OverviewIcon from '../OverviewIcon';
import { Storage } from '../../utility/Storage';
import './styles.scss';
import { SHARED_PASSAGE } from '../../../utility/const';

import AllOverViewIcons from '../AllOverviewIcons';
import CharmBraceletPagination from './charmBraceletPagination';

// timeout variable
let doRefresh = null;
let reorderedQuestions = null;

/**
 * Renders the top questions overview
 * */
class CharmBracelet extends PureComponent {
  static displayName = 'CharmBracelet';

  responses = {};

  static propTypes = {
    /**
     * Flag for displaying desktop view or mobile view icon.
     * The submit button icon is different on desktop vs mobile.
     * */
    isMobile: PropTypes.bool,
    /**
     * Handler for closing the questions menu and the main menu.
     * */
    closeMenuContent: PropTypes.func,
    /**
     * Flag for the player being in the preview mode or nor.
     * */
    preview: PropTypes.bool,
    isImpersonating: PropTypes.bool,
    showNavigationNumbers: PropTypes.bool,
  };

  static defaultProps = {
    isMobile: false,
    closeMenuContent: null,
    preview: false,
    isReordering: false,
    onOrderChange: () => {},
    isImpersonating: false,
    onItemChanged: () => {},
    showNavigationNumbers: true,
  };

  constructor(props) {
    super(props);

    const {
      learnosityData: { app, items: propItems },
      isMobile,
      preview,
    } = props;
    const items = isMobile ? app.getItems() : propItems;
    const itemKeys = Object.keys(items);
    let answeredQuestions = [];

    if (!preview) {
      answeredQuestions = itemKeys.filter(
        (itemKey) => items[itemKey].attempt_status !== 'not_attempted',
      );
    }

    this.state = {
      refresh: false,
      loading: true,
      answeredQuestions,
    };

    /**
     * Current item index used for first time
     * */
    this.currentItemIndex = 1;
    /**
     * Icons width - must include the connection line
     * Used to calculate max nr of icons allowed - based on container width
     * */
    this.iconWidth = 46;
  }

  componentDidMount() {
    /**
     * Hackish fix to support transition from mobile to desktop
     * */
    setTimeout(() => {
      this.readyListener();
    }, 0);
  }

  /**
   * Init method
   * */
  readyListener() {
    const {
      learnosityData: { items, activity, app },
      preview,
      isReordering,
      isImpersonating,
      onItemChanged,
    } = this.props;
    app.on('item:changed', this.itemChanged);

    if (!preview) {
      app.on('item:setAttemptedResponse', this.itemAttempted);
    }

    this.refresh();
    /**
     * Define store for persisting user local settings
     * */
    const { session_id } = activity;
    this.bookmarkStore = new Storage(
      `${BOOKMARKED_QUESTIONS}-${session_id}${isImpersonating ? '-i' : ''}`,
    );

    this.itemReferences = Object.keys(items);

    this.itemsLength = this.itemReferences.length;

    /*
     * currentItem must be took with app.getCurrentItem() to support mobile component unmount
     * every time the menu is closed.
     * */
    this.currentItem = app.getCurrentItem();
    const currentItemReference = this.currentItem && this.currentItem.reference;
    this.currentItemIndex = this.getIndexByReference(currentItemReference);
    this.sharedFeaturesTree = this.createSharedFeaturesTree(items);

    window.addEventListener('resize', this.resizeHandler);
    window.addEventListener('orientationchange', this.resizeHandler);

    // this events to handle keyboard correctly from the reordering, we change order of questions so "Learnosity"
    // events navigate on original order not on the changed one.
    // but we need to cancel Learnosity navigation first, then use array of references we got after reordering
    reorderedQuestions = null;
    isReordering ? this.addArrowsEvents() : this.removeArrowsEvents();

    this.setState({
      loading: false,
      currentItemReference,
    });
    onItemChanged(currentItemReference);
  }

  navigateWithArrows(e) {
    if (reorderedQuestions) {
      if (
        e.keyCode === KEY_CODES.leftArrow ||
        e.keyCode === KEY_CODES.rightArrow
      ) {
        if (e.keyCode === KEY_CODES.leftArrow) {
          this.onPreviousClick();
        } else {
          this.onNextClick();
        }
        e.stopImmediatePropagation();
      }
    }
  }

  addArrowsEvents() {
    document.body.addEventListener(
      'keydown',
      this.navigateWithArrows.bind(this),
    );
  }

  removeArrowsEvents() {
    document.body.removeEventListener('keydown', this.navigateWithArrows);
  }

  createSharedFeaturesTree(items) {
    const findSharedPassages = (features) =>
      features
        .filter((item) => item.type === SHARED_PASSAGE)
        .map((feature) => feature.feature_id);

    return this.itemReferences.reduce(
      (acc, reference, index) => {
        const isLast = this.itemsLength - 1 === index;
        const isFirst = index === 0;
        const nextReference = this.itemReferences[index + 1];
        const previousReference = this.itemReferences[index - 1];
        const currentItemSharedPassages = findSharedPassages(
          items[reference].features,
        );
        const { nodes, nextShared, previousShared } = acc;
        const prevNode = nodes[nodes.length - 1];

        const isSharesPassageWithNextQuestion = () => {
          if (isLast) return false;
          const nextItemSharedPassages = findSharedPassages(
            items[nextReference].features,
          );
          return (
            currentItemSharedPassages &&
            nextItemSharedPassages &&
            currentItemSharedPassages.some((passageId) =>
              nextItemSharedPassages.includes(passageId),
            )
          );
        };

        const isSharesPassageWithPreviousQuestion = () => {
          if (isFirst) return false;
          const previousItemSharedPassages =
            previousReference &&
            findSharedPassages(items[previousReference].features);
          return (
            currentItemSharedPassages &&
            previousItemSharedPassages &&
            currentItemSharedPassages.some((passageId) =>
              previousItemSharedPassages.includes(passageId),
            )
          );
        };

        if (
          !isSharesPassageWithNextQuestion() &&
          !isSharesPassageWithPreviousQuestion()
        ) {
          nodes.push(reference);

          // when previous item is end of set and current is start
        } else if (
          prevNode &&
          isArray(prevNode) &&
          !isSharesPassageWithPreviousQuestion()
        ) {
          nodes[nodes.length] = [reference];

          // otherwise keep adding nodes to set
        } else if (prevNode && isArray(prevNode)) {
          prevNode.push(reference);
        } else {
          nodes[nodes.length] = [reference];
        }

        isSharesPassageWithNextQuestion() && nextShared.push(reference);
        isSharesPassageWithPreviousQuestion() && previousShared.push(reference);

        return acc;
      },
      { nodes: [], nextShared: [], previousShared: [] },
    );
  }

  componentWillUnmount() {
    const {
      learnosityData: { app },
      preview,
    } = this.props;

    app.off('item:changing', this.itemChanged);

    if (!preview) {
      app.off('item:setAttemptedResponse', this.itemAttempted);
    }

    window.removeEventListener('resize', this.resizeHandler);
    window.removeEventListener('orientationchange', this.resizeHandler);
    this.removeArrowsEvents();
  }

  /*
   * Calculate allowed icons per page based on the available space
   *
   * Remove 20 pixels because the container gets smaller
   * after learnosity has done loading the navigation bar
   * */
  getIconsPerPage = (container) =>
    Math.floor((container.offsetWidth - 20) / this.iconWidth);

  /**
   * Called when learnosity is setting the current item
   * @param {number} itemIndex The item index
   * */
  itemChanged = (itemIndex) => {
    const {
      learnosityData: { app },
      onItemChanged,
    } = this.props;

    this.currentItem = app.getCurrentItem();
    this.currentItemIndex = reorderedQuestions
      ? reorderedQuestions.indexOf(this.currentItem.reference) + 1
      : itemIndex + 1;

    const currentItemReference = this.currentItem && this.currentItem.reference;
    this.setState(
      (prevState) => ({
        refresh: !prevState.refresh,
        currentItemReference,
      }),
      () => {
        const el = document.getElementById(
          `question-nav-${currentItemReference}`,
        );
        requestAnimationFrame(() => {
          if (el && el.focus) {
            el.focus();
          }
        });
      },
    );
    onItemChanged(currentItemReference);
  };

  itemAttempted = (reference) => {
    const { answeredQuestions } = this.state;

    if (!answeredQuestions.includes(reference)) {
      this.setState((prevState) => ({
        answeredQuestions: [...prevState.answeredQuestions, reference],
      }));
    }
  };

  /**
   * Get index by reference
   * @param {string} reference The question reference
   * */
  getIndexByReference = (reference) => {
    const {
      learnosityData: { items },
    } = this.props;
    return Object.keys(items).indexOf(reference) + 1;
  };

  /*
   * Resize handler - resize components
   * */
  resizeHandler = () => {
    /*
     * call handler 2 seconds after resizing has stopped
     * The only times we need to do a manual refresh is when the screen is
     * too small, we can't show all the numbers in the navigation and the starting
     * index has been adjusted to something > 1 for a better UX
     *
     * decides whether to re-organize the nav buttons, this is actually done by
     * toggling the "refresh" state variable
     */
    this.refresh();
    const maxIndex = this.getMaxIndex();

    if (this.getFromIndex(maxIndex) > 1 || maxIndex < this.currentItemIndex) {
      clearTimeout(doRefresh);
      doRefresh = setTimeout(() => {
        this.setState((prevState) => ({ refresh: !prevState.refresh }));
      }, 2000);
    }
  };

  /**
   * Refresh component
   * Used to re-calculate the iconsPerPage variable
   * */
  refresh = () => {
    const containerElement = document.querySelector('.top-questions-overview');
    if (containerElement) {
      this.iconsPerPage = this.getIconsPerPage(containerElement);
    }
  };

  /**
   * Get maximum index
   * */
  getMaxIndex = () =>
    this.itemsLength > this.iconsPerPage ? this.iconsPerPage : this.itemsLength;

  /**
   * Get from index
   * @param {number} maxIndex Maximum index
   * */
  getFromIndex = (maxIndex) => {
    if (this.currentItemIndex > maxIndex) {
      return this.currentItemIndex - this.iconsPerPage + 1;
    }
    return 1;
  };

  /**
   * Click icon event handler - callback
   * @param {object} event The click event handler
   * @param {string} reference The clicked icon question reference
   * */
  onIconClick = (event, reference) => {
    this.goToQuestionByReference(reference);
  };

  goToQuestion = (index) => {
    const reference = reorderedQuestions
      ? reorderedQuestions[index]
      : this.itemReferences[index];
    this.goToQuestionByReference(reference);
  };

  goToQuestionByReference(reference) {
    const {
      learnosityData: { app },
    } = this.props;
    const itemsObj = app.items();
    itemsObj.goto(reference);
  }

  onPreviousClick = () => {
    this.goToQuestion(this.currentItemIndex - 2);
  };

  onNextClick = () => {
    this.goToQuestion(this.currentItemIndex);
  };

  /**
   * Keydown handler to allow use of
   * - arrows to navigate left and right
   * - spacebar and return to select question
   */
  keyDownHandler = (event, reference) => {
    // only handle pressing of spacebar and return, arrows are handled hereunder.
    if ([37, 39, 13, 32].includes(event.keyCode)) {
      const preventDefaults = () => {
        // Make sure to stop event bubbling
        event.preventDefault();
        event.stopPropagation();
      };

      /**
       * for Return or Spacebar, select whatever question item is
       * focused
       */
      if ([13, 32].includes(event.keyCode)) {
        preventDefaults();
        this.onIconClick(_, reference);
        return;
      }

      const {
        learnosityData: { app },
      } = this.props;
      const references = Object.keys(app.getItems());
      const indexOfReference = references.indexOf(reference);
      /**
       * Do nothing if
       * - we hit the left arrow key and we are on the first item
       * - we hit the right arrow key and we are on the last item
       * - we hit either the left or right arrow but the current reference isn't
       * on the list of items
       */
      if (
        (event.keyCode === 37 && indexOfReference === 0) ||
        (event.keyCode === 39 && indexOfReference === references.length - 1) ||
        indexOfReference === -1
      ) {
        return;
      }

      preventDefaults();

      let nextReference = references[indexOfReference + 1];
      if (event.keyCode === 37) {
        nextReference = references[indexOfReference - 1];
      }

      this.onIconClick(_, nextReference);
    }
  };

  renderByPage() {
    const { answeredQuestions } = this.state;
    const {
      isMobile,
      closeMenuContent,
      preview,
      showDoubleArrow,
      showNavigationNumbers,
    } = this.props;

    const icons = [];
    const bookmarkedQuestions = this.bookmarkStore.getQuestions();
    const { itemReferences } = this;

    for (let i = 1; i <= itemReferences.length; i++) {
      const reference = itemReferences[i - 1];
      const isAnswered = answeredQuestions.includes(reference);
      const isBookmarked = bookmarkedQuestions.includes(reference);
      const isSelected = this.currentItemIndex === i;

      const icon = (
        <OverviewIcon
          isMobile={isMobile}
          index={i}
          reference={reference}
          isAnswered={isAnswered}
          isSelected={isSelected}
          hasConnection={this.sharedFeaturesTree.nextShared.includes(reference)}
          hasPrevConnection={this.sharedFeaturesTree.previousShared.includes(
            reference,
          )}
          isBookmarked={isBookmarked}
          onClick={this.onIconClick}
          keyDownHandler={this.keyDownHandler}
          key={`icon-${reference}`}
          closeMenuContent={closeMenuContent}
        />
      );

      icons.push(icon);
    }

    return isMobile ? (
      <div className="question-list charm-bracelet" role="tablist">
        {icons}
      </div>
    ) : (
      <CharmBraceletPagination
        showDoubleArrow={showDoubleArrow}
        currentQuestionIndex={this.currentItemIndex - 1}
        goToQuestion={this.goToQuestion}
        onPreviousClick={this.onPreviousClick}
        onNextClick={this.onNextClick}
        isPreview={preview}
        showNavigationNumbers={showNavigationNumbers}
      >
        {icons}
      </CharmBraceletPagination>
    );
  }

  renderAll() {
    return (
      <AllOverViewIcons
        nodes={this.sharedFeaturesTree.nodes}
        currentItemReference={this.state.currentItemReference}
        onIconClick={this.onIconClick}
        onDragEnd={(...params) => {
          reorderedQuestions = params[3];
          this.currentItemIndex =
            params[3].indexOf(this.currentItem.reference) + 1;
          this.props.onOrderChange(...params);
        }}
      />
    );
  }
  render() {
    const { loading } = this.state;
    const { isReordering } = this.props;
    if (loading) {
      return null;
    }

    return isReordering ? this.renderAll() : this.renderByPage();
  }
}

export default CharmBracelet;
